<template>
  <div class="agent-form">
    <toolbar
      backRoute="/dashboard/agents"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="agent-item-delete"
      :saveButtonLabel="saveButtonLabel"
      saveButtonEvent="agent-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="agent-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="name">Name:</label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="phone">Phone:</label>

                <validation-provider
                  rules="required"
                  name="phone"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.phone"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="phone">Phone (2):</label>

                <d-input
                  v-model="data.phone_2"/>
              </div>

              <div class="form-group">
                <label for="phone">Phone (3):</label>

                <d-input
                  v-model="data.phone_3" />
              </div>

              <div class="form-group">
                <label for="email">E-mail:</label>

                <validation-provider
                  rules="email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.email"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">E-mail (2):</label>
                <d-input v-model="data.email_2" />
              </div>

              <div class="form-group">
                <label for="email">E-mail (3):</label>
                <d-input v-model="data.email_3" />
              </div>

              <div class="form-group">
                <label for="email">Website:</label>

                <d-input-group>
                  <d-input
                    v-model="data.website" />
                  <d-input-group-addon append>
                    <d-btn
                      theme="primary"
                      :disabled="!data.website"
                      @click.prevent="openUrl(data.website)">
                      <i class="material-icons">launch</i>
                    </d-btn>
                  </d-input-group-addon>
                </d-input-group>
              </div>

              <div class="form-group">
                <label for="name">Handler frequency:</label>

                <d-input
                  v-model="data.handler_frequency" />
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                  <label for="notes">Type:</label>

                  <div class="mt-1">
                    <d-form-radio
                      inline
                      name="type"
                      v-model="data.type"
                      value="AGENT">
                      Agent
                    </d-form-radio>
                    <d-form-radio
                      inline
                      name="type"
                      v-model="data.type"
                      value="HANDLER">
                      Handler
                    </d-form-radio>

                    <span class="ml-5">
                      <d-checkbox
                        inline
                        v-model="data.fbo"
                        toggle>
                        FBO
                      </d-checkbox>
                    </span>
                  </div>
              </div>

              <div class="form-group">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  :rows="31" />
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>

      <airports-list
        v-if="loadedData"
        referenceType="AGENT"
        :referenceId="data.id"
        itemName="agent" />
    </d-container>
  </div>
</template>

<script>
import AirportsList from './../form-parts/Airports.vue';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import HasUrlField from './../../mixins/HasUrlField.vue';

export default {
  name: 'agent-form',
  mixins: [
    HasChangesDetection,
    HasUrlField
  ],
  components: {
    'airports-list': AirportsList
  },
  computed: {
    allDataLoaded () {
      return this.loadedData;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        if (this.data.type === 'AGENT') {
          return 'Add new agent';
        }

        return 'Add new handler';
      }

      if (this.data.type === 'AGENT') {
        return 'Edit agent';
      }

      return 'Edit handler';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      if (this.data.type === 'AGENT') {
        return 'Delete agent';
      }

      return 'Delete handler';
    },
    saveButtonLabel () {
      if (this.data.type === 'AGENT') {
        return 'Save agent';
      }

      return 'Save handler';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        phone: '',
        phone_2: '',
        phone_3: '',
        email: '',
        email_2: '',
        email_3: '',
        handler_frequency: '',
        notes: '',
        website: '',
        type: 'AGENT',
        fbo: false
      },
      loadError: false,
      loadedData: false,
      removeInProgress: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.$bus.$on('agent-item-delete', this.removeAgent);
    this.$bus.$on('agent-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/handlers/item/',
        instanceFields: ['id', 'name', 'phone', 'phone_2', 'phone_3', 'email', 'email_2', 'email_3', 'handler_frequency', 'website', 'notes', 'type', 'fbo'],
        loadedKey: 'loadedData',
        successAction: (data) => {
          this.data.fbo = !!data.fbo;
        }
      });
    },
    removeAgent () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/handlers/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Agent removed',
        successText: 'The selected agent has been removed',
        confirmTitle: 'Do you really want to remove this agent?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/agents');
          this.$bus.$emit('calendar-reload-handlers');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['agent-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/handlers/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/handlers/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Agent added' : 'Agent saved',
        successText: 'The agent has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/agents');
          this.$bus.$emit('calendar-reload-handlers');
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('agent-item-delete', this.removeAgent);
    this.$bus.$off('agent-item-save', this.validateForm);
  }
}
</script>
